import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { walletApi } from '../../../services/api/walletApi';
import '../accountTheme.css';
import './WalletScreen.css';

const TX_LABELS = {
  ADMIN_CREDIT: 'Admin credit',
  ADMIN_DEBIT: 'Admin debit',
  DEPOSIT: 'Deposit',
  DEPOSIT_BONUS: 'Bonus',
  WITHDRAW: 'Withdrawal',
  GAME_BET: 'Game bet',
  GAME_WIN: 'Game win',
  GAME_REFUND: 'Refund',
};

function getTxLabel(tx) {
  if (tx.note && ['DEPOSIT', 'DEPOSIT_BONUS', 'ADMIN_CREDIT'].includes(tx.type)) {
    return tx.note;
  }
  return TX_LABELS[tx.type] || tx.type;
}

function WalletScreen({ user, onBack, onOpenDeposit, onOpenWithdraw }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await walletApi.transactions();
      setTransactions(data.transactions ?? []);
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const balance = user?.walletBalance ?? 0;

  return (
    <Box className="bz-page">
      <Box className="bz-topbar">
        <IconButton size="small" onClick={onBack} className="bz-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="bz-topbar__title">Wallet</Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box className="bz-scroll">
        <Box className="bz-balance-hero wallet-balance-hero">
          <Typography className="bz-balance-label">Total balance</Typography>
          <Typography className="bz-balance-value">
            ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </Typography>
        </Box>

        <Box className="bz-actions-row">
          <button type="button" className="bz-action-btn bz-action-btn--deposit" onClick={onOpenDeposit}>
            <span className="bz-action-btn__label">+ Deposit</span>
          </button>
          <button type="button" className="bz-action-btn bz-action-btn--withdraw" onClick={onOpenWithdraw}>
            <span className="bz-action-btn__label">− Withdraw</span>
          </button>
        </Box>

        <Typography className="bz-section-title">All transactions</Typography>
        <Box className="bz-card">
          {loading ? (
            <Box className="bz-empty"><CircularProgress size={28} sx={{ color: '#ff6b6b' }} /></Box>
          ) : transactions.length === 0 ? (
            <Typography className="bz-empty">No transactions yet</Typography>
          ) : (
            transactions.map((tx) => {
              const credit = ['ADMIN_CREDIT', 'DEPOSIT', 'DEPOSIT_BONUS', 'GAME_WIN', 'GAME_REFUND'].includes(tx.type);
              return (
                <Box key={tx.id} className="bz-tx-row">
                  <Box>
                    <Typography className="bz-tx-type">{getTxLabel(tx)}</Typography>
                    <Typography className="bz-tx-note">
                      {tx.note && !['DEPOSIT', 'DEPOSIT_BONUS', 'ADMIN_CREDIT'].includes(tx.type)
                        ? tx.note
                        : new Date(tx.createdAt).toLocaleString('en-IN')}
                    </Typography>
                    <Typography className="bz-tx-note">
                      Balance: ₹{tx.balanceAfter?.toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography className={credit ? 'bz-tx-amount--credit' : 'bz-tx-amount--debit'}>
                    {credit ? '+' : '-'}₹{Math.abs(tx.amount).toFixed(2)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default WalletScreen;
